import { useState } from 'react';
import Section from '../common/Section';
import { ADD_ONS, BADGE_OPTIONS } from '../../constants/profileAddOns';

const MAX_ITEMS = 20;

const ADDON_META = {
  careerHighlightsEnabled: { title: 'Career Highlights', desc: 'Show key moments and best performances on your profile.', icon: '✨' },
  achievementsEnabled: { title: 'Achievements', desc: 'Add personal achievements and accomplishments.', icon: '🏆' },
  milestonesEnabled: { title: 'Milestones', desc: 'Track milestones like 500 runs, 50 wickets, etc.', icon: '🎯' },
  playerBadgesEnabled: { title: 'Player Badges', desc: 'Display badges that represent your playing style and strengths.', icon: '🏷️' },
  levelBadgesEnabled: { title: 'Level Badges', desc: 'Auto-calculated performance level badge.', icon: '📈' },
  awardsEnabled: { title: 'Awards', desc: 'Add awards you’ve won across tournaments and seasons.', icon: '🥇' },
  matchParticipationEnabled: { title: 'Match Participation', desc: 'Show match history and summary stats.', icon: '🗓️' },
  joinTournamentsEnabled: { title: 'Join Tournaments', desc: 'Show a CTA so organizers can invite you to tournaments.', icon: '🏟️' },
  joinTeamsEnabled: { title: 'Join Teams', desc: 'Show a CTA so teams can invite you to join.', icon: '👥' },
  mediaEnabled: { title: 'Media Gallery', desc: 'Add match photos/videos to boost your profile.', icon: '📸' },
};

const AddOnCard = ({ title, children }) => (
  <div className="rounded-4 border bg-white p-3 p-md-4 shadow-sm">
    <div className="d-flex align-items-center justify-content-between mb-3">
      <div>
        <div className="small text-uppercase fw-semibold text-primary">Settings</div>
        <div className="h6 mb-0">{title}</div>
      </div>
      <span className="badge rounded-pill text-bg-light border">Step 6</span>
    </div>
    {children}
  </div>
);

const Field = ({ label, children, hint }) => (
  <div>
    <div className="fw-semibold small mb-1">{label}</div>
    {children}
    {hint ? <div className="text-muted small mt-1">{hint}</div> : null}
  </div>
);

const renderListEditor = ({ items = [], onAdd, onRemove, onChange, fields, limitError, addLabel = 'Add item' }) => (
  <div className="d-grid gap-2">
    {items.map((item, index) => (
      <div className="rounded-4 border p-3 bg-light" key={item.id || `${index}`}>
        <div className="row g-2 align-items-end">
          {fields.map((field) => (
            <div className={field.col || 'col-md-6'} key={field.key}>
              <Field label={field.label || field.placeholder}>
                <input
                  className="form-control"
                  placeholder={field.placeholder}
                  value={item[field.key] || ''}
                  type={field.type || 'text'}
                  onChange={(e) => onChange(index, field.key, e.target.value)}
                />
              </Field>
            </div>
          ))}
          <div className="col-12 d-flex justify-content-end">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(index)}>
              Remove
            </button>
          </div>
        </div>
      </div>
    ))}

    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-1">
      <button type="button" className="btn btn-outline-primary" onClick={onAdd} disabled={items.length >= MAX_ITEMS}>
        {addLabel}
      </button>

      <div className="text-muted small">
        {items.length}/{MAX_ITEMS} items
      </div>
    </div>

    {limitError && <div className="text-danger small mt-1">{limitError}</div>}
  </div>
);

const AccordionItem = ({
  id,
  title,
  desc,
  icon,
  enabled,
  isOpen,
  onToggleOpen,
  onToggleEnabled,
  children,
}) => {
  return (
    <div className="border rounded-4 overflow-hidden mb-2 bg-white shadow-sm">
      {/* Header */}
      <button
        type="button"
        className="w-100 text-start border-0 bg-white"
        onClick={onToggleOpen}
        style={{ padding: '1rem' }}
      >
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className="rounded-3 d-flex align-items-center justify-content-center bg-light border"
              style={{ width: 42, height: 42, flex: '0 0 auto' }}
              aria-hidden="true"
            >
              <span className="fs-5">{icon}</span>
            </div>

            <div>
              <div className="fw-semibold d-flex align-items-center gap-2">
                {title}
                {enabled ? (
                  <span className="badge rounded-pill text-bg-success">Enabled</span>
                ) : (
                  <span className="badge rounded-pill text-bg-light border">Off</span>
                )}
              </div>
              <div className="text-muted small">{desc}</div>
            </div>
          </div>

          {/* Right side: Switch + Arrow */}
          <div className="d-flex align-items-center gap-3">
            <div
              className="form-check form-switch m-0"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <input
                className="form-check-input"
                type="checkbox"
                checked={enabled}
                onChange={onToggleEnabled}
              />
            </div>

            {/* Arrow */}
            <span
              className={`addon-chevron ${isOpen ? 'open' : ''}`}
              aria-hidden="true"
            >
              ▼
            </span>
          </div>
        </div>
      </button>

      {/* Body */}
      <div
        className="bg-light"
        style={{
          maxHeight: isOpen ? 2000 : 0,
          overflow: 'hidden',
          transition: 'max-height 200ms ease',
        }}
      >
        <div style={{ padding: isOpen ? '1rem' : '0 1rem' }}>
          {!enabled ? (
            <div className="rounded-4 border bg-white p-3">
              <div className="fw-semibold mb-1">Turn this on to add details</div>
              <div className="text-muted small mb-0">
                Enable “{title}” using the switch above, then add your content here.
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
};

const ExtrasSection = ({ formData, onToggleAddOn, onAddOnFieldChange, onManageListItem, errors = {} }) => {
  const { addOns = {} } = formData;

  // default open first item
  const firstKey = Object.keys(ADD_ONS)[0];
  const [openKey, setOpenKey] = useState(firstKey);

  return (
    <Section
      eyebrow="Step 6"
      title="Profile Enhancements"
      description="Enable optional sections and add details. You can change these anytime."
    >
      {Object.entries(ADD_ONS).map(([key, label]) => {
        const meta = ADDON_META[key] || { title: label, desc: '', icon: '✨' };
        const enabled = Boolean(addOns[key]);
        const isOpen = openKey === key;

        return (
          <AccordionItem
            key={key}
            id={key}
            title={meta.title}
            desc={meta.desc}
            icon={meta.icon}
            enabled={enabled}
            isOpen={isOpen}
            onToggleOpen={() => setOpenKey(isOpen ? null : key)}
            onToggleEnabled={() => {
              onToggleAddOn(key, label);
              setOpenKey(key); // open when toggled
            }}
          >
            <AddOnCard title={meta.title}>
              {/* Career Highlights */}
              {key === 'careerHighlightsEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  {renderListEditor({
                    items: formData.highlights,
                    onAdd: () => onManageListItem('highlights', 'add'),
                    onRemove: (index) => onManageListItem('highlights', 'remove', index),
                    onChange: (index, field, value) => onManageListItem('highlights', 'change', index, field, value),
                    limitError: errors.highlights,
                    addLabel: 'Add highlight',
                    fields: [
                      { key: 'title', placeholder: 'Highlight title', label: 'Title', col: 'col-md-6' },
                      { key: 'year', placeholder: '2024', type: 'number', label: 'Year', col: 'col-md-3' },
                      { key: 'description', placeholder: 'Short description', label: 'Description', col: 'col-md-12' },
                    ],
                  })}
                </>
              )}

              {/* Achievements */}
              {key === 'achievementsEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  {renderListEditor({
                    items: formData.achievements,
                    onAdd: () => onManageListItem('achievements', 'add'),
                    onRemove: (index) => onManageListItem('achievements', 'remove', index),
                    onChange: (index, field, value) => onManageListItem('achievements', 'change', index, field, value),
                    limitError: errors.achievements,
                    addLabel: 'Add achievement',
                    fields: [
                      { key: 'title', placeholder: 'Achievement title', label: 'Title', col: 'col-md-7' },
                      { key: 'category', placeholder: 'Batting / Bowling / Fielding', label: 'Category', col: 'col-md-5' },
                      { key: 'description', placeholder: 'Short description', label: 'Description', col: 'col-md-12' },
                    ],
                  })}
                </>
              )}

              {/* Milestones */}
              {key === 'milestonesEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  {renderListEditor({
                    items: formData.milestones,
                    onAdd: () => onManageListItem('milestones', 'add'),
                    onRemove: (index) => onManageListItem('milestones', 'remove', index),
                    onChange: (index, field, value) => onManageListItem('milestones', 'change', index, field, value),
                    limitError: errors.milestones,
                    addLabel: 'Add milestone',
                    fields: [
                      { key: 'label', placeholder: 'Milestone label', label: 'Label', col: 'col-md-6' },
                      { key: 'value', placeholder: '500', label: 'Value', col: 'col-md-3' },
                      { key: 'unit', placeholder: 'runs / wickets', label: 'Unit', col: 'col-md-3' },
                    ],
                  })}
                </>
              )}

              {/* Awards */}
              {key === 'awardsEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  {renderListEditor({
                    items: formData.awards,
                    onAdd: () => onManageListItem('awards', 'add'),
                    onRemove: (index) => onManageListItem('awards', 'remove', index),
                    onChange: (index, field, value) => onManageListItem('awards', 'change', index, field, value),
                    limitError: errors.awards,
                    addLabel: 'Add award',
                    fields: [
                      { key: 'name', placeholder: 'Award name', label: 'Name', col: 'col-md-6' },
                      { key: 'tournament', placeholder: 'Tournament', label: 'Tournament', col: 'col-md-4' },
                      { key: 'year', placeholder: '2024', type: 'number', label: 'Year', col: 'col-md-2' },
                    ],
                  })}
                </>
              )}

              {/* Player Badges */}
              {key === 'playerBadgesEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  <div className="d-flex flex-wrap gap-2">
                    {BADGE_OPTIONS.map((badge) => {
                      const selected = (formData.badges || []).some((b) => b.code === badge.code);
                      return (
                        <button
                          type="button"
                          key={badge.code}
                          className={`btn rounded-pill ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() =>
                            onManageListItem('badges', selected ? 'toggleRemove' : 'toggleAdd', null, null, badge)
                          }
                        >
                          <span className="me-2" aria-hidden="true">{badge.icon}</span>
                          {badge.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-muted small mt-2">Selected: {(formData.badges || []).length}</div>
                </>
              )}

              {/* Level Badge */}
              {key === 'levelBadgesEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  <div className="rounded-4 border bg-light p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="small text-uppercase fw-semibold text-primary">Current level</div>
                        <div className="h5 mb-0">{formData.level?.levelName || 'Rookie'}</div>
                      </div>
                      <span className="badge rounded-pill text-bg-light border">{formData.level?.points || 0} pts</span>
                    </div>
                    <div className="text-muted small mt-2">
                      Points = matches×2 + runs×0.05 + wickets×1.5 + awards×3
                    </div>
                  </div>
                </>
              )}

              {/* Match Participation */}
              {key === 'matchParticipationEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  <div className="form-check form-switch mb-3">
                    <input
                      id={`showHistory-${key}`}
                      className="form-check-input"
                      type="checkbox"
                      checked={Boolean(formData.matchParticipation?.showHistory)}
                      onChange={(e) => onAddOnFieldChange('matchParticipation', 'showHistory', e.target.checked)}
                    />
                    <label htmlFor={`showHistory-${key}`} className="form-check-label">
                      Show match history
                    </label>
                  </div>

                  <div className="row g-2">
                    {['matches', 'runs', 'wickets', 'best'].map((field) => (
                      <div className="col-md-3" key={field}>
                        <Field label={field.toUpperCase()}>
                          <input
                            className="form-control"
                            value={formData.matchParticipation?.summary?.[field] || ''}
                            readOnly
                            disabled
                          />
                        </Field>
                      </div>
                    ))}
                  </div>
                  <div className="text-muted small mt-2">Stats are server-calculated and refresh after scorecard updates.</div>
                </>
              )}

              {/* Join tournaments */}
              {key === 'joinTournamentsEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  <div className="rounded-4 border bg-light p-3">
                    <div className="fw-semibold mb-1">This will add a CTA on your profile</div>
                    <div className="text-muted small">“Invite to Tournament” will appear for organizers.</div>
                  </div>
                </>
              )}

              {/* Join teams */}
              {key === 'joinTeamsEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  <div className="rounded-4 border bg-light p-3">
                    <div className="fw-semibold mb-1">This will add a CTA on your profile</div>
                    <div className="text-muted small">“Invite to Team” will appear for teams.</div>
                  </div>
                </>
              )}

              {/* Media gallery */}
              {key === 'mediaEnabled' && (
                <>
                  <div className="text-muted small mb-3">{meta.desc}</div>
                  {renderListEditor({
                    items: formData.mediaGallery,
                    onAdd: () => onManageListItem('mediaGallery', 'add'),
                    onRemove: (index) => onManageListItem('mediaGallery', 'remove', index),
                    onChange: (index, field, value) => onManageListItem('mediaGallery', 'change', index, field, value),
                    limitError: errors.mediaGallery,
                    addLabel: 'Add media',
                    fields: [
                      { key: 'type', placeholder: 'image / video', label: 'Type', col: 'col-md-3' },
                      { key: 'url', placeholder: 'https://...', label: 'URL', col: 'col-md-6' },
                      { key: 'title', placeholder: 'Title', label: 'Title', col: 'col-md-3' },
                    ],
                  })}
                </>
              )}
            </AddOnCard>
          </AccordionItem>
        );
      })}
    </Section>
  );
};

export default ExtrasSection;
